/**
 * Owns the visitor's A/B bucket cookie (`ab_bucket`), and assigns it — only with cookie consent.
 * Without the cookie Fastly sends no bucket and the visitor sees the original, so visitors before or without
 * consent (and crawlers) always get the original and aren't counted. When the visitor consents — on any page —
 * they're given a random bucket, which applies from their next request (nothing changes on screen). Withdrawing
 * consent removes the cookie. Mounted once in `_app`. Written by JS (so not HttpOnly); Fastly only forwards
 * `control` | `treatment`.
 */
import Cookies from 'js-cookie';
import { useEffect } from 'react';
import { type Bucket, isBucket } from './bucket';
import { isAbTestingEnabled } from './experiments';
import { CONSENT_EVENT, hasAbConsent } from './consent';

const COOKIE = 'ab_bucket';

const randomBucket = (): Bucket => (Math.random() < 0.5 ? 'control' : 'treatment');

export function syncBucketCookie() {
  const consent = hasAbConsent();
  if (consent === undefined) {
    // OneTrust hasn't loaded yet — its OneTrustGroupsUpdated event will call us again.
    return;
  }

  if (!consent) {
    // Even where A/B testing is off, so a bucket left from when it was on still goes when consent does.
    Cookies.remove(COOKIE, { path: '/' });
    return;
  }

  if (!isAbTestingEnabled()) {
    return;
  }

  const current = Cookies.get(COOKIE);
  const bucket = isBucket(current) ? current : randomBucket();
  // Rewritten every time, so the 30-day expiry rolls forward while the visitor keeps coming back.
  Cookies.set(COOKIE, bucket, {
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
    expires: 30,
    path: '/',
  });
}

export function useBucketCookie() {
  useEffect(() => {
    syncBucketCookie();
    window.addEventListener(CONSENT_EVENT, syncBucketCookie);
    return () => window.removeEventListener(CONSENT_EVENT, syncBucketCookie);
  }, []);
}
